import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { statusToneClasses, statusToneSolidClass } from '../../lib/statusTone';

const dangerTone = statusToneClasses('danger');
const warningTone = statusToneClasses('warning');
const dangerSolid = statusToneSolidClass('danger');

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95',
  {
    variants: {
      variant: {
        primary: 'bg-brand-blue-600 text-base-white hover:bg-brand-blue-700',
        secondary:
          'border border-brand-blue-500 bg-surface text-brand-blue-600 hover:bg-brand-blue-50',
        outline:
          'border border-[var(--border-default)] bg-surface text-text-primary hover:bg-neutral-50',
        ghost: 'text-brand-blue-600 hover:bg-brand-blue-600/10',
        subtle: 'bg-neutral-100 text-text-secondary hover:bg-neutral-100/80',
        danger: cn(
          'border',
          dangerTone.subtleBorder,
          dangerTone.text,
          'bg-base-white',
          'hover:bg-error-100'
        ),
        warning: cn(
          'border',
          warningTone.subtleBorder,
          warningTone.text,
          warningTone.subtleBackground,
          'hover:bg-warning-100/75'
        ),
        destructive: cn(dangerSolid.background, dangerSolid.text, 'hover:bg-error-500/90'),
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 py-2',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
